// Transitions PR-5 — "Ask AI" path-coach modal. Cloned from
// RecordPaymentExplainModal / WaterfallExplainModal: fires the initial
// explanation automatically on open (question null, no history), keeps a
// running staff-question / coach-answer transcript, and lets the user ask
// follow-ups (the most recent 12 turns ride the request as `history`). The
// wizard's context snapshot rides every request; the grounding numbers are
// always loaded server-side.

import { useEffect, useState } from "react";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useExplainTransitionPath } from "@/hooks/portfolioAdmin/useLoanTransitions";
import type {
  TransitionExplainContext,
  TransitionExplainTurn,
} from "@/api/portfolioAdmin/loanTransitionsApi";

export interface TransitionExplainModalProps {
  loanId: string;
  /** Snapshot of the wizard's / card's context at open time. */
  context: TransitionExplainContext;
  onClose: () => void;
}

export function TransitionExplainModal({
  loanId,
  context,
  onClose,
}: TransitionExplainModalProps) {
  const explain = useExplainTransitionPath();
  const [turns, setTurns] = useState<TransitionExplainTurn[]>([]);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const ask = (q: string | null) => {
    // History = the transcript BEFORE this question; the server caps at 12
    // turns — keep the most recent so a long chat degrades gracefully.
    const recent = turns.slice(-12);
    const history = recent.length > 0 ? recent : null;
    if (q) {
      setTurns((cur) => [...cur, { role: "user", content: q }]);
      setQuestion("");
    }
    setErrorMessage(null);
    setPending(true);
    explain.mutate(
      { loanId, body: { question: q, history, context } },
      {
        onSuccess: (res) =>
          setTurns((cur) => [...cur, { role: "assistant", content: res.answer }]),
        onError: (err) =>
          setErrorMessage(
            err instanceof Error && err.message
              ? err.message
              : "The coach couldn't reply. Try again.",
          ),
        onSettled: () => setPending(false),
      },
    );
  };

  // Auto-fire the initial explanation once on mount (callers mount this
  // modal only while it's open).
  useEffect(() => {
    ask(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = question.trim();

  return (
    <Modal
      open
      onClose={onClose}
      title="Ask AI about this exit path"
      description="Explains what this path does and what happens next — grounded in this loan's terms and records."
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="transition-explain-modal">
        <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
          {turns.map((t, i) => (
            <li key={i} data-testid={`explain-turn-${t.role}`}>
              <p className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
                {t.role === "user" ? "You asked" : "Coach"}
              </p>
              <div
                className={
                  "rounded-md border border-border-light p-space-3 text-body whitespace-pre-wrap " +
                  (t.role === "user" ? "bg-white-warm" : "bg-white-pure")
                }
              >
                {t.content}
              </div>
            </li>
          ))}
        </ol>

        {pending && (
          <p className="text-body text-gray-mid italic m-0" data-testid="explain-thinking">
            Reviewing this loan's records…
          </p>
        )}

        {errorMessage && (
          <p role="alert" className="text-caption text-status-error m-0">
            {errorMessage}
          </p>
        )}

        <div className="flex flex-col gap-space-1">
          <Textarea
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a follow-up — e.g. What happens if the buyer misses the tender date?"
            aria-label="Follow-up question"
            disabled={pending}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={!trimmed || pending}
              onClick={() => ask(trimmed)}
              data-testid="explain-ask-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
